/**
 * 📷 **AR 자리 이름을 만드는 곳 — 여기 하나.**
 *
 * ## 왜 한 곳인가
 * AR 한 장면은 자리를 **여섯 개**나 쓴다(타깃·오버레이·영상·포스터·Spine 셋). 그리고 그 이름을
 * **일곱 화면**이 같이 쓴다 — 카드 편집기·오버레이 편집기·타깃 고르기·책 편집기·책 쪽 편집·
 * 아이 화면(arData)·지우기 목록.
 *
 * 각자 이어 붙이면 모양이 한 번 바뀌는 날 **한쪽만 따라간다.** 관리자에는 「올렸다」고
 * 표시되는데 아이 화면에서는 [AR로 보기] 가 아예 안 뜨거나, 지우기가 파일을 남긴다.
 * 오류는 안 난다 — 그냥 조용히 어긋난다.
 *
 * 실제로 그 상태였다(2026-08-30): 일곱 곳이 각자 짜고 있었고, shared 에는 만드는 함수가
 * **아예 없었다**. `assetKeyParity.test.ts` 가 앞으로 이 규칙을 지킨다.
 *
 * ## 장면 id 는 두 갈래다
 * · 카드 AR — `chumseongdae` 처럼 짧은 이름
 * · 책 쪽 AR — `<책>.<쪽>`(점이 들어간다)
 * 그래서 **점을 구분자로 쓰면 안 된다** — 뒤에서 자르면 책 쪽 AR 이 깨진다.
 * 지금 규칙은 「접두사 + id」이고, Spine 만 뒤에 확장자가 붙는다.
 */

/** 인식할 그림(.mind). 이게 없으면 아이 화면에 [AR로 보기] 가 안 뜬다. */
export function arTargetKey(sceneId: string): string {
  return `ar.target.${sceneId}`;
}

/** 인식했을 때 얹을 그림(스트립). */
export function arOverlayKey(sceneId: string): string {
  return `ar.overlay.${sceneId}`;
}

/** 인식했을 때 틀 영상(투명 WebM). */
export function arVideoKey(sceneId: string): string {
  return `ar.video.${sceneId}`;
}

/**
 * 🧊 **AR 보기 「④ 3D 모델」**(.glb) — 카메라가 알아보면 그 위에 실물 3D 가 서고 손으로 돌려 본다(2026-09-14).
 * 층 3D 자리(`ar.layer.model.<층>.<장면>`)와 다르다 — 마법 꾸미기 없이 장면 하나에 하나.
 */
export function arModelKey(sceneId: string): string {
  return `ar.model.${sceneId}`;
}

/** 무엇을 비출지 보여주는 안내 그림. */
export function arPosterKey(sceneId: string): string {
  return `ar.poster.${sceneId}`;
}

/** Spine 애니메이션 — 셋이 다 있어야 켜진다. */
export function arSpineKey(sceneId: string, part: "json" | "atlas" | "png"): string {
  return `ar.spine.${sceneId}.${part}`;
}

/** Spine 텍스처 여러 장의 **앞자리**(`…tex.` 로 시작하는 것을 모은다). */
export function arSpineTexPrefix(sceneId: string): string {
  return `ar.spine.${sceneId}.tex.`;
}

/**
 * 한 장면이 쓰는 자리 **전부**. 지울 때·훑을 때 쓴다.
 *
 * 🔴 지우기가 이 목록보다 좁으면 **파일이 남는다** — 장면은 사라졌는데 R2 에는 그림이 남아
 * 아무도 안 쓰는 채로 용량만 먹는다(그리고 아무 표시도 안 난다).
 * 텍스처(`tex.*`)는 개수를 모르므로 여기 없다 — 접두사로 따로 모은다.
 */
export function arSceneKeys(sceneId: string): string[] {
  return [
    arTargetKey(sceneId),
    arOverlayKey(sceneId),
    arVideoKey(sceneId),
    arPosterKey(sceneId),
    arModelKey(sceneId),
    arSpineKey(sceneId, "json"),
    arSpineKey(sceneId, "atlas"),
    arSpineKey(sceneId, "png"),
  ];
}

/**
 * 🪄 **층(layer) 그림** — 「누르면 일어나는 마법」(docs/build/35)의 스티커 한 장.
 *
 * 🔴 **장면 id 를 맨 뒤에 둔다.** 책 쪽 id(`poljjak.p07`)에는 점이 있다. 층 id 를 뒤에 붙이면
 * 「어디까지가 장면이고 어디부터가 층인지」를 뒤에서 자를 수 없다. 앞에서부터 읽는다:
 * `ar.layer.img.` + 층id + `.` + (나머지 전부 = 장면 id).
 * 그래서 층 id 는 **점이 없어야** 한다 — `isArLayerId` 가 지킨다.
 */
export const AR_LAYER_ID_RE = /^[a-z0-9_]{1,24}$/;
export function isArLayerId(v: unknown): v is string {
  return typeof v === "string" && AR_LAYER_ID_RE.test(v);
}
export function arLayerImgKey(sceneId: string, layerId: string): string {
  return `ar.layer.img.${layerId}.${sceneId}`;
}

/**
 * 🧊 층의 **3D 파일**(.glb) 자리 — 관리자 「3D 층」이 올린다. 책 뷰어는 띠(`arLayerImgKey`, 제작 도구가 이 파일로 굽는다)만
 * 쓰고(A안), 카메라 AR 은 이 파일을 읽어 책 위에 진짜 3D 를 세운다(B안, `apps/web/src/ar/arData.ts`). 3D 를 못 받으면 띠로 돌아간다.
 * 게시할 때는 층 그림과 같이 나간다(`magicPublishPlan`).
 */
export function arLayerModelKey(sceneId: string, layerId: string): string {
  return `ar.layer.model.${layerId}.${sceneId}`;
}
export function arLayerModelOf(key: string): { sceneId: string; layerId: string } | null {
  const m = /^ar\.layer\.model\.([a-z0-9_]{1,24})\.(.+)$/.exec(key);
  return m ? { layerId: m[1]!, sceneId: m[2]! } : null;
}

/** 층 그림 자리 이름 → `{ 장면, 층 }`. 아니면 `null`. */
export function arLayerOf(key: string): { sceneId: string; layerId: string } | null {
  const m = /^ar\.layer\.img\.([a-z0-9_]{1,24})\.(.+)$/.exec(key);
  return m ? { layerId: m[1]!, sceneId: m[2]! } : null;
}

/** 이 자리 이름이 어느 장면의 것인가 — 아니면 `null`. */
export function arSceneIdOf(key: string): string | null {
  const m = /^ar\.(?:target|overlay|video|poster|model)\.(.+)$/.exec(key);
  if (m) return m[1]!;
  // Spine 은 뒤에 확장자가 붙는다. 장면 id 에 점이 있을 수 있어 **뒤에서** 자른다.
  const sp = /^ar\.spine\.(.+)\.(?:json|atlas|png)$/.exec(key);
  if (sp) return sp[1]!;
  const tex = /^ar\.spine\.(.+)\.tex\..+$/.exec(key);
  if (tex) return tex[1]!;
  // 층 그림·3D 파일은 장면 id 가 **맨 뒤**라 앞에서부터 읽는다.
  return arLayerOf(key)?.sceneId ?? arLayerModelOf(key)?.sceneId ?? null;
}

/**
 * 🗑 **한 장면에 딸린 자리 전부** — 있는 자리 이름 목록에서 골라낸다.
 *
 * `arSceneKeys` 는 「있을 수 있는 고정 자리」만 준다. 층 그림과 Spine 텍스처는 **몇 개인지
 * 모르므로** 거기 없다. 지우기가 그 둘을 빠뜨리면 장면은 사라졌는데 R2 에 그림이 남는다 —
 * 그리고 아무 표시도 안 난다. 지울 때는 **이 함수 하나**로 모은다.
 */
export function arSceneKeysIn(allKeys: readonly string[], sceneId: string): string[] {
  const fixed = new Set(arSceneKeys(sceneId));
  return allKeys.filter((k) => fixed.has(k) || arSceneIdOf(k) === sceneId);
}
